import { useState, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';

export interface ApiToken {
  id: string;
  tokenPrefix: string;
  name: string;
  description: string | null;
  createdBy: string;
  ownerType: 'user' | 'team';
  subjectRef: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
}

export interface CreateTokenInput {
  name: string;
  description?: string;
  ownerType: 'user' | 'team';
  subjectRef?: string;
  scopes: string[];
  expiresAt?: string;
}

async function apiCall<T>(
  baseUrl: string,
  path: string,
  fetchApi: typeof window.fetch,
  options?: RequestInit,
): Promise<T> {
  const response = await fetchApi(`${baseUrl}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      body.error?.message ?? body.message ?? `API error: ${response.status}`,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/** Hook for listing/managing the current user's tokens. */
export function useApiTokens(refreshKey: number) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  return useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('api-tokens');
    const data = await apiCall<{ tokens: ApiToken[] }>(
      baseUrl,
      '/tokens',
      fetchApi.fetch.bind(fetchApi),
    );
    return data.tokens;
  }, [refreshKey]);
}

/** Hook for creating a token — returns the create function and a state for the one-time secret. */
export function useCreateToken() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const createToken = useCallback(
    async (
      input: CreateTokenInput,
    ): Promise<{ token: string; metadata: ApiToken }> => {
      setLoading(true);
      setError(undefined);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('api-tokens');
        const result = await apiCall<{ token: string; metadata: ApiToken }>(
          baseUrl,
          '/tokens',
          fetchApi.fetch.bind(fetchApi),
          { method: 'POST', body: JSON.stringify(input) },
        );
        return result;
      } catch (err: any) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  return { createToken, loading, error };
}

/** Hook for rotating a token — returns the rotate function. */
export function useRotateToken() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const rotateToken = useCallback(
    async (id: string): Promise<{ token: string; metadata: ApiToken }> => {
      setLoading(true);
      setError(undefined);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('api-tokens');
        return await apiCall<{ token: string; metadata: ApiToken }>(
          baseUrl,
          `/tokens/${id}/rotate`,
          fetchApi.fetch.bind(fetchApi),
          { method: 'POST', body: '{}' },
        );
      } catch (err: any) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  return { rotateToken, loading, error };
}

/** Hook for revoking a token. */
export function useRevokeToken() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);

  const revokeToken = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('api-tokens');
        await apiCall<void>(
          baseUrl,
          `/tokens/${id}`,
          fetchApi.fetch.bind(fetchApi),
          { method: 'DELETE' },
        );
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  return { revokeToken, loading };
}

/** Hook for admin token management. */
export function useAdminTokens(refreshKey: number) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  return useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('api-tokens');
    const data = await apiCall<{ tokens: ApiToken[] }>(
      baseUrl,
      '/admin/tokens',
      fetchApi.fetch.bind(fetchApi),
    );
    return data.tokens;
  }, [refreshKey]);
}

export function useAdminActions() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);

  const revokeToken = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('api-tokens');
        await apiCall<void>(
          baseUrl,
          `/admin/tokens/${id}`,
          fetchApi.fetch.bind(fetchApi),
          { method: 'DELETE' },
        );
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  const revokeAll = useCallback(async (): Promise<number> => {
    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('api-tokens');
      const result = await apiCall<{ revokedCount: number }>(
        baseUrl,
        '/admin/tokens/revoke-all',
        fetchApi.fetch.bind(fetchApi),
        { method: 'POST', body: '{}' },
      );
      return result.revokedCount;
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  return { revokeToken, revokeAll, loading };
}
